import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTellerConnect } from "teller-connect-react";
import { Building2, Crown, Link2, RefreshCw, Trash2, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import HelpButton from "../components/HelpButton";

// Format an ISO timestamp as "just now / 5m ago / 2h ago / Feb 12" style
function relTime(iso) {
  if (!iso) return "Never synced";
  const then = new Date(iso).getTime();
  const s = Math.floor((Date.now() - then) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function ConnectionRow({ conn, onSync, onDisconnect, syncing }) {
  const isReauth = conn.status === "reauth_required";
  const isRevoked = conn.status === "revoked";
  return (
    <div
      className="nb-card p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`bank-conn-${conn.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-11 h-11 rounded-lg border-2 border-[#1a1a1a] bg-white flex items-center justify-center shrink-0">
          <Building2 size={22} strokeWidth={2.75} />
        </div>
        <div className="min-w-0">
          <div className="font-display font-black text-base truncate">
            {conn.institution?.name || "Bank"}
          </div>
          <div className="text-xs font-bold text-[#4a4a4a] flex items-center gap-1.5 mt-0.5">
            {isReauth ? (
              <>
                <AlertCircle size={13} className="text-red-600" />
                <span className="text-red-700">Needs reconnect</span>
              </>
            ) : isRevoked ? (
              <span className="text-[#7a7a7a]">Disconnected</span>
            ) : (
              <>
                <CheckCircle2 size={13} className="text-green-600" />
                <span>{conn.accounts_count || 0} account{conn.accounts_count === 1 ? "" : "s"} · last sync {relTime(conn.last_sync_at)}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2 shrink-0">
        {!isRevoked && (
          <button
            onClick={() => onSync(conn.id)}
            disabled={syncing === conn.id}
            className="nb-btn nb-btn-sm"
            data-testid={`bank-sync-btn-${conn.id}`}
          >
            <RefreshCw size={14} className={syncing === conn.id ? "animate-spin" : ""} />
            {syncing === conn.id ? "Syncing…" : "Sync now"}
          </button>
        )}
        {!isRevoked && (
          <button
            onClick={() => onDisconnect(conn.id, conn.institution?.name)}
            className="nb-btn nb-btn-sm"
            data-testid={`bank-disconnect-btn-${conn.id}`}
          >
            <Trash2 size={14} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function BankSync() {
  const { user } = useAuth();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(null);
  const [saving, setSaving] = useState(false);

  const isPro = user?.plan === "paid";
  const tellerAppId = process.env.REACT_APP_TELLER_APPLICATION_ID || "";
  const tellerEnv = process.env.REACT_APP_TELLER_ENVIRONMENT || "sandbox";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/teller/connections");
      setConnections(data || []);
    } catch (e) {
      if (e?.response?.status !== 402) {
        console.error("Failed to load connections", e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isPro) load();
    else setLoading(false);
  }, [isPro, load]);

  const onSuccess = useCallback(
    async (enrollment) => {
      setSaving(true);
      try {
        const { data } = await api.post("/teller/enrollments", {
          access_token: enrollment.accessToken,
          enrollment_id: enrollment.enrollment?.id,
          institution: enrollment.enrollment?.institution,
          user: enrollment.user,
        });
        toast.success(
          `Connected ${data.institution?.name || "your bank"} · ${data.transactions_added} txns imported`
        );
        await load();
      } catch (e) {
        const msg = e?.response?.data?.detail || "Failed to save bank connection";
        toast.error(typeof msg === "string" ? msg : "Failed to save bank connection");
      } finally {
        setSaving(false);
      }
    },
    [load]
  );

  const { open, ready } = useTellerConnect({
    applicationId: tellerAppId,
    environment: tellerEnv,
    products: ["balance", "transactions"],
    onSuccess,
    onExit: () => {},
    onFailure: (f) => toast.error(f?.message || "Bank connection failed"),
  });

  const handleSync = async (cid) => {
    setSyncing(cid);
    try {
      const { data } = await api.post(`/teller/connections/${cid}/sync`);
      toast.success(`Sync done · ${data.added} new · ${data.skipped} already imported`);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        toast.error("Bank connection expired — please reconnect");
        await load();
      } else {
        toast.error(e?.response?.data?.detail || "Sync failed");
      }
    } finally {
      setSyncing(null);
    }
  };

  const handleDisconnect = async (cid, name) => {
    if (!window.confirm(`Disconnect ${name || "this bank"}? Your transaction history stays saved.`)) return;
    try {
      await api.delete(`/teller/connections/${cid}`);
      toast.success("Bank disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to disconnect");
    }
  };

  // ---------- Pro gate ----------
  if (!isPro) {
    return (
      <div className="space-y-4" data-testid="banksync-locked">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-black">Live bank sync</h1>
          <HelpButton id="bank_sync" />
        </div>
        <div
          className="nb-card-lg p-8 text-center"
          style={{ background: "linear-gradient(135deg,#fdecc8,#d9d4f1)" }}
        >
          <Crown size={48} strokeWidth={2.5} className="mx-auto mb-3" />
          <div className="font-display font-black text-2xl mb-2">Pro feature</div>
          <div className="text-sm font-medium text-[#4a4a4a] mb-5 max-w-md mx-auto">
            Connect Huntington, Chase, Bank of America, and 5,000+ banks. Transactions flow in
            automatically every 6 hours — no more typing them by hand.
          </div>
          <Link to="/upgrade" className="nb-btn nb-btn-dark inline-flex" data-testid="banksync-upgrade-btn">
            <Crown size={16} /> Upgrade to Pro
          </Link>
        </div>
      </div>
    );
  }

  // ---------- Not configured yet ----------
  if (!tellerAppId) {
    return (
      <div className="space-y-4" data-testid="banksync-not-configured">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-black">Live bank sync</h1>
          <HelpButton id="bank_sync" />
        </div>
        <div className="nb-card-lg p-6" style={{ background: "#fff9c4" }}>
          <div className="font-display font-black text-xl mb-2">Setup pending</div>
          <div className="text-sm font-medium text-[#4a4a4a] mb-3">
            Bank sync is almost ready! An admin still needs to add the Teller Application ID.
          </div>
          <div className="text-xs font-bold text-[#4a4a4a]">
            1. Sign up at <a className="underline" href="https://teller.io/signup" target="_blank" rel="noreferrer">teller.io</a>
            <br />2. Copy the Application ID into <code>REACT_APP_TELLER_APPLICATION_ID</code>
            <br />3. Drop the cert + private key into the backend
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="banksync-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="label">Auto-import</div>
          <h1 className="font-display text-3xl font-black leading-tight">Live bank sync</h1>
        </div>
        <HelpButton id="bank_sync" />
      </div>

      <div className="nb-card p-4" style={{ background: "#e6f2ff" }}>
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg border-2 border-[#1a1a1a] bg-white flex items-center justify-center shrink-0">
            <Link2 size={18} strokeWidth={2.75} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-display font-black text-base leading-tight">
              Add a bank
            </div>
            <div className="text-xs font-bold text-[#4a4a4a] mt-0.5">
              Your login is handled by Teller — SyncBook only sees transactions.
              {tellerEnv !== "production" && (
                <span className="ml-1 uppercase text-[10px] px-1.5 py-0.5 rounded border border-[#1a1a1a] bg-white">
                  {tellerEnv}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={() => open()}
            disabled={!ready || saving}
            className="nb-btn nb-btn-dark shrink-0"
            data-testid="bank-connect-btn"
          >
            {saving ? "Saving…" : ready ? "Connect bank" : "Loading…"}
          </button>
        </div>
      </div>

      {loading ? (
        <div className="nb-card p-4 text-sm font-medium text-[#4a4a4a]">Loading connections…</div>
      ) : connections.length === 0 ? (
        <div className="nb-card-lg p-6 text-center" data-testid="bank-empty">
          <Building2 size={40} strokeWidth={2.5} className="mx-auto mb-3 text-[#7a7a7a]" />
          <div className="font-display font-black text-lg mb-1">No banks connected yet</div>
          <div className="text-sm font-medium text-[#4a4a4a]">
            Tap “Connect bank” above to pull in your real transactions.
          </div>
          {tellerEnv === "sandbox" && (
            <div className="text-xs font-bold text-[#4a4a4a] mt-3 bg-[#fdecc8] rounded-lg p-2 inline-block">
              Sandbox test login: <code>username</code> / <code>password</code>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {connections.map((c) => (
            <ConnectionRow
              key={c.id}
              conn={c}
              syncing={syncing}
              onSync={handleSync}
              onDisconnect={handleDisconnect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
